import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import { useTransitionState } from 'react-transition-state';

import { useDropTarget } from '../../ui/dnd';
import { Tooltip, type TooltipProps } from '../../ui/tooltip';
import * as styles from './resize-panel.css';

export interface ResizeHandleProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  resizing: boolean;
  open: boolean;
  minWidth: number;
  maxWidth: number;
  resizeHandlePos: 'left' | 'right';
  resizeHandleOffset?: number;
  resizeHandleVerticalPadding?: number;
  onOpen: (open: boolean) => void;
  onResizing: (resizing: boolean) => void;
  onWidthChange: (width: number) => void;
  onWidthChanged?: (width: number) => void;
  tooltip?: TooltipProps['content'];
  tooltipShortcut?: TooltipProps['shortcut'];
  tooltipOptions?: Partial<Omit<TooltipProps, 'content' | 'shortcut'>>;
  tooltipShortcutClassName?: string;
  dropTargetOptions?: Parameters<typeof useDropTarget>[0];
}

export type PanelLayoutTransition = 'continuous' | 'settled' | 'overlay';

/**
 * Where a docked panel that does not push its neighbour along its slide stands. `covering`: the
 * panel lies over its neighbour's end: a settled panel while it slides in and before it slides
 * out, an overlay panel as long as it is open. `sliding`: the panel is moving.
 */
export interface PanelSlide {
  covering: boolean;
  sliding: boolean;
}

export interface ResizePanelProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  resizing: boolean;
  open: boolean;
  floating?: boolean;
  floatingOffset?: number;
  minWidth: number;
  maxWidth: number;
  resizeHandlePos: 'left' | 'right';
  resizeHandleOffset?: number;
  resizeHandleVerticalPadding?: number;
  resizeHandleTooltip?: TooltipProps['content'];
  resizeHandleTooltipShortcut?: TooltipProps['shortcut'];
  resizeHandleTooltipShortcutClassName?: string;
  resizeHandleTooltipOptions?: Partial<
    Omit<TooltipProps, 'content' | 'shortcut'>
  >;
  resizeHandleDropTargetOptions?: Parameters<typeof useDropTarget>[0];
  enableAnimation?: boolean;
  /**
   * How the neighbour follows the panel opening and closing: `continuous` lays it out on every
   * frame of the slide, `settled` once, `overlay` never: the panel lies over the neighbour's end.
   * A neighbour that is costly to resize takes `settled`, one that must not move takes `overlay`.
   */
  layoutTransition?: PanelLayoutTransition;
  /** Tells the neighbour where the slide stands, in time for the same paint. */
  onSlide?: (slide: PanelSlide) => void;
  width: number;
  unmountOnExit?: boolean;
  onOpen: (open: boolean) => void;
  onResizing: (resizing: boolean) => void;
  onWidthChange: (width: number) => void;
  onWidthChanged?: (width: number) => void;
}

const ResizeHandle = ({
  className,
  resizing,
  minWidth,
  maxWidth,
  resizeHandlePos,
  resizeHandleOffset,
  resizeHandleVerticalPadding,
  dropTargetOptions,
  open,
  onOpen,
  onResizing,
  onWidthChange,
  onWidthChanged,
  tooltip,
  tooltipShortcut,
  tooltipOptions,
  tooltipShortcutClassName,
  ...rest
}: ResizeHandleProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const onResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      let resized = false;
      const handle = event.currentTarget;
      const pointerId = event.pointerId;
      const panelContainer = ref.current?.parentElement;
      if (!panelContainer) return;

      // The handle keeps the pointer for the whole drag. A frame the pointer
      // crosses (e.g. an App preview) would otherwise take the moves and the
      // release, and the drag would never end. The capture alone starts only
      // with the next move this page gets, and an 8px handle is left before
      // that - so a shield covers the frames for as long as the drag lasts.
      const shield = document.createElement('div');
      shield.className = styles.dragShield;
      document.body.append(shield);
      handle.setPointerCapture(pointerId);

      // add cursor style to body
      document.body.style.cursor = 'col-resize';

      const { left: anchorLeft, right: anchorRight } =
        panelContainer.getBoundingClientRect();

      let lastWidth: number;
      function onPointerMove(e: PointerEvent) {
        if (e.pointerId !== pointerId) return;
        e.preventDefault();
        const newWidth = Math.min(
          maxWidth,
          Math.max(
            resizeHandlePos === 'right'
              ? e.clientX - anchorLeft
              : anchorRight - e.clientX,
            minWidth
          )
        );
        lastWidth = newWidth;
        onWidthChange(newWidth);
        onResizing(true);
        resized = true;
      }
      function onPointerEnd(e: PointerEvent) {
        if (e.pointerId !== pointerId) return;
        handle.removeEventListener('pointermove', onPointerMove);
        handle.removeEventListener('pointerup', onPointerEnd);
        handle.removeEventListener('pointercancel', onPointerEnd);
        if (handle.hasPointerCapture(pointerId))
          handle.releasePointerCapture(pointerId);
        shield.remove();
        // if not resized, toggle sidebar
        if (!resized && e.type === 'pointerup') {
          onOpen(false);
        }
        onResizing(false);
        lastWidth && onWidthChanged?.(lastWidth);
        document.body.style.cursor = '';
      }

      handle.addEventListener('pointermove', onPointerMove);
      handle.addEventListener('pointerup', onPointerEnd);
      handle.addEventListener('pointercancel', onPointerEnd);
    },
    [
      maxWidth,
      resizeHandlePos,
      minWidth,
      onWidthChange,
      onResizing,
      onWidthChanged,
      onOpen,
    ]
  );

  const { dropTargetRef } = useDropTarget(dropTargetOptions, [
    dropTargetOptions,
  ]);

  return (
    <Tooltip
      content={tooltip}
      shortcut={tooltipShortcut}
      shortcutClassName={tooltipShortcutClassName}
      {...tooltipOptions}
    >
      <div
        {...rest}
        data-testid="resize-handle"
        ref={node => {
          ref.current = node;
          dropTargetRef.current = node;
        }}
        style={assignInlineVars({
          [styles.resizeHandleOffsetVar]: `${resizeHandleOffset ?? 0}px`,
          [styles.resizeHandleVerticalPadding]: `${
            resizeHandleVerticalPadding ?? 0
          }px`,
        })}
        className={clsx(styles.resizeHandleContainer, className)}
        data-handle-position={resizeHandlePos}
        data-resizing={resizing}
        data-open={open}
        onPointerDown={onResizeStart}
      >
        <div className={styles.resizerInner} />
      </div>
    </Tooltip>
  );
};

/** How long the panel slides open and closed, in ms. */
export const RESIZE_PANEL_SLIDE_MS = 280;
/** Duration and easing of the slide: quick off the mark, then a long settle (ease-out cubic). */
export const RESIZE_PANEL_SLIDE_TIMING = `${RESIZE_PANEL_SLIDE_MS}ms cubic-bezier(0.33, 1, 0.68, 1)`;
// A panel that moves by its transform alone ends the slide with that transition: a slow first frame
// starts it late. The timer only ends a slide whose transition never ran.
const ownSlideTimeout = RESIZE_PANEL_SLIDE_MS + 700;

export const ResizePanel = forwardRef<HTMLDivElement, ResizePanelProps>(
  function ResizePanel(
    {
      children,
      className,
      resizing,
      minWidth,
      maxWidth,
      width,
      floating,
      floatingOffset,
      enableAnimation = true,
      layoutTransition = 'continuous',
      onSlide,
      open,
      unmountOnExit,
      onOpen,
      onResizing,
      onWidthChange,
      onWidthChanged,
      resizeHandlePos,
      resizeHandleOffset,
      resizeHandleVerticalPadding,
      resizeHandleTooltip,
      resizeHandleTooltipShortcut,
      resizeHandleTooltipShortcutClassName,
      resizeHandleTooltipOptions,
      resizeHandleDropTargetOptions,
      onTransitionEnd,
      ...rest
    },
    ref
  ) {
    const safeWidth = Math.min(maxWidth, Math.max(minWidth, width));
    const settled = layoutTransition === 'settled';
    const overlay = layoutTransition === 'overlay';
    const ownSlide = settled || overlay;
    const [{ status }, toggle, endTransition] = useTransitionState({
      timeout: ownSlide ? ownSlideTimeout : RESIZE_PANEL_SLIDE_MS,
      preExit: settled,
    });
    useLayoutEffect(() => {
      toggle(open);
    }, [open, toggle]);
    const covering =
      !floating &&
      (overlay
        ? open
        : settled && (status === 'entering' || status === 'preExit'));
    const sliding =
      !floating &&
      (settled || overlay) &&
      enableAnimation &&
      !resizing &&
      (status === 'entering' || status === 'exiting');
    useLayoutEffect(() => {
      onSlide?.({ covering, sliding });
    }, [covering, onSlide, sliding]);
    const handleTransitionEnd = useCallback(
      (event: React.TransitionEvent<HTMLDivElement>) => {
        onTransitionEnd?.(event);
        if (
          ownSlide &&
          event.target === event.currentTarget &&
          event.propertyName === 'transform'
        ) {
          endTransition();
        }
      },
      [endTransition, onTransitionEnd, ownSlide]
    );
    return (
      <div
        {...rest}
        ref={ref}
        style={assignInlineVars({
          [styles.panelWidthVar]: `${safeWidth}px`,
          [styles.floatingOffsetVar]: `${floatingOffset ?? 4}px`,
          [styles.animationTimeout]: RESIZE_PANEL_SLIDE_TIMING,
        })}
        className={clsx(className, styles.root)}
        data-open={open}
        data-transition-state={status}
        data-layout-transition={layoutTransition}
        onTransitionEnd={handleTransitionEnd}
        data-is-floating={floating}
        data-handle-position={resizeHandlePos}
        data-enable-animation={enableAnimation && !resizing}
      >
        <div className={styles.content}>
          {!(status === 'exited' && unmountOnExit !== false) && children}
        </div>
        <ResizeHandle
          resizeHandlePos={resizeHandlePos}
          resizeHandleOffset={resizeHandleOffset}
          resizeHandleVerticalPadding={resizeHandleVerticalPadding}
          tooltip={resizeHandleTooltip}
          tooltipOptions={resizeHandleTooltipOptions}
          tooltipShortcut={resizeHandleTooltipShortcut}
          tooltipShortcutClassName={resizeHandleTooltipShortcutClassName}
          maxWidth={maxWidth}
          minWidth={minWidth}
          onOpen={onOpen}
          onResizing={onResizing}
          onWidthChange={onWidthChange}
          onWidthChanged={onWidthChanged}
          open={open}
          resizing={resizing}
          dropTargetOptions={resizeHandleDropTargetOptions}
        />
      </div>
    );
  }
);
